import React, { useState, useEffect, useContext, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { AuthContext } from '../../contexts/AuthContext';
import LoadingSpinner from '../../components/LoadingSpinner';
import './Admin.css';

export default function ProductManagement() {
  const { api, BASE_URL } = useContext(AuthContext);
  const navigate = useNavigate();

  const [products, setProducts]       = useState([]);
  const [loading, setLoading]         = useState(true);
  const [error, setError]             = useState('');
  const [refreshing, setRefreshing]   = useState(false);
  const [togglingId, setTogglingId]   = useState(null);

  // ── Inline edit state ──
  // editingCell = { id, field } — which cell is being edited
  const [editingCell, setEditingCell] = useState(null);
  const [editValue, setEditValue]     = useState('');
  const [savingId, setSavingId]       = useState(null);
  const inputRef = useRef(null);

  useEffect(() => { fetchProducts(); }, []);

  // Focus input when editing starts
  useEffect(() => {
    if (editingCell && inputRef.current) {
      inputRef.current.focus();
      inputRef.current.select();
    }
  }, [editingCell]);

  const fetchProducts = async () => {
    try {
      setError('');
      const response = await api.get('/products');
      const data = Array.isArray(response.data)
        ? response.data
        : (response.data?.products || response.data?.data || []);
      setProducts(data);
    } catch (err) {
      setError('Failed to load products');
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };

  const handleRefresh = () => { setRefreshing(true); fetchProducts(); };

  const handleDelete = async (id, name) => {
    if (!window.confirm(`Delete "${name}"?`)) return;
    try {
      await api.delete(`/products/${id}`);
      setProducts(products.filter(p => p.id !== id));
    } catch (err) {
      alert(err.response?.data?.message || 'Failed to delete product');
    }
  };

  // ── Toggle active/inactive ──
  const handleToggleStatus = async (product) => {
    const newStatus = !product.is_active;
    setTogglingId(product.id);
    setProducts(prev =>
      prev.map(p => p.id === product.id ? { ...p, is_active: newStatus } : p)
    );
    try {
      await api.patch(`/products/${product.id}`, { is_active: newStatus });
    } catch (err) {
      setProducts(prev =>
        prev.map(p => p.id === product.id ? { ...p, is_active: product.is_active } : p)
      );
      alert(err.response?.data?.message || 'Failed to update status');
    } finally {
      setTogglingId(null);
    }
  };

  // ── Start editing a cell ──
  const startEdit = (product, field) => {
    setEditingCell({ id: product.id, field });
    setEditValue(String(product[field] ?? ''));
  };

  // ── Cancel editing ──
  const cancelEdit = () => {
    setEditingCell(null);
    setEditValue('');
  };

  // ── Save inline edit ──
  const saveEdit = async (product) => {
    const { field } = editingCell;
    const raw = editValue.trim();

    // Validate
    if (raw === '') { cancelEdit(); return; }

    const numVal = parseFloat(raw);
    if (isNaN(numVal) || numVal < 0) {
      alert('Please enter a valid number');
      return;
    }
    if (field === 'discount_percent' && numVal > 100) {
      alert('Discount cannot exceed 100%');
      return;
    }

    // Same value — no need to save
    if (String(product[field]) === raw) { cancelEdit(); return; }

    setSavingId(product.id);

    // Optimistic update
    setProducts(prev =>
      prev.map(p => p.id === product.id ? { ...p, [field]: numVal } : p)
    );
    cancelEdit();

    try {
      await api.patch(`/products/${product.id}`, { [field]: numVal });
    } catch (err) {
      // Revert
      setProducts(prev =>
        prev.map(p => p.id === product.id ? { ...p, [field]: product[field] } : p)
      );
      alert(err.response?.data?.message || 'Failed to update');
    } finally {
      setSavingId(null);
    }
  };

  // ── Handle Enter / Escape in input ──
  const handleKeyDown = (e, product) => {
    if (e.key === 'Enter')  saveEdit(product);
    if (e.key === 'Escape') cancelEdit();
  };

  const getImageUrl = (imageUrl) => {
    if (!imageUrl) return null;
    if (imageUrl.startsWith('http://') || imageUrl.startsWith('https://')) return imageUrl;
    const cleanPath = imageUrl.startsWith('/') ? imageUrl : `/${imageUrl}`;
    return `${BASE_URL}${cleanPath}`;
  };

  const getCategoryLabel = (category) => {
    if (category === 'fruits') return '🍎 Fruits';
    if (category === 'meat')   return '🥩 Meat';
    return '🥬 Vegetables';
  };

  const getCategoryEmoji = (category) => {
    if (category === 'fruits') return '🍎';
    if (category === 'meat')   return '🥩';
    return '🥬';
  };

  const getDiscountedPrice = (price, discountPercent) => {
    const p = parseFloat(price);
    const d = parseFloat(discountPercent);
    if (!d || isNaN(d) || d <= 0) return null;
    return (p - (p * d) / 100).toFixed(2);
  };

  if (loading) return <LoadingSpinner message="Loading products..." />;

  return (
    <div className="admin-product-management">
      <div className="container">
        <div className="page-header">
          <div>
            <h1 className="page-title">
              <span className="title-icon">🥬</span>
              Product Management
            </h1>
            <p className="page-subtitle">Manage your product catalog</p>
          </div>
          <div className="header-actions">
            <button className="btn btn-outline btn-sm" onClick={handleRefresh} disabled={refreshing}>
              {refreshing ? '🔄 Refreshing...' : '🔄 Refresh'}
            </button>
            <button className="btn btn-primary" onClick={() => navigate('/admin/products/add')}>
              ➕ Add Product
            </button>
          </div>
        </div>

        {error && (
          <div className="alert alert-error">
            <span className="alert-icon">⚠️</span>
            <span className="alert-text">{error}</span>
          </div>
        )}

        {/* ── Tip banner ── */}
        <div className="inline-edit-tip">
          💡 <strong>Price</strong> aur <strong>Discount</strong> pe click karke directly edit kar sakte hain
        </div>

        <div className="products-count">
          <span className="count-badge">{products.length} Products</span>
          {products.filter(p => parseFloat(p.discount_percent) > 0).length > 0 && (
            <span className="count-badge discount-count-badge">
              🏷️ {products.filter(p => parseFloat(p.discount_percent) > 0).length} On Sale
            </span>
          )}
        </div>

        {products.length === 0 ? (
          <div className="empty-state">
            <div className="empty-state-icon">🥬</div>
            <h2 className="empty-state-title">No products found</h2>
            <p className="empty-state-text">Start by adding your first product</p>
            <button className="btn btn-primary" onClick={() => navigate('/admin/products/add')}>
              ➕ Add Product
            </button>
          </div>
        ) : (
          <div className="products-table-container">
            <table className="products-table">
              <thead>
                <tr>
                  <th>Image</th>
                  <th>Name</th>
                  <th>Category</th>
                  <th>Price ✏️</th>
                  <th>Discount ✏️</th>
                  <th>Stock</th>
                  <th>Status</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {products.map((product) => {
                  const imageUrl        = getImageUrl(product.image_url);
                  const emoji           = getCategoryEmoji(product.category);
                  const discountedPrice = getDiscountedPrice(product.price, product.discount_percent);
                  const hasDiscount     = discountedPrice !== null;
                  const isToggling      = togglingId === product.id;
                  const isSaving        = savingId === product.id;

                  const isEditingPrice    = editingCell?.id === product.id && editingCell?.field === 'price';
                  const isEditingDiscount = editingCell?.id === product.id && editingCell?.field === 'discount_percent';

                  return (
                    <tr key={product.id} className={`${hasDiscount ? 'row-has-discount' : ''} ${isSaving ? 'row-saving' : ''}`}>

                      {/* Image */}
                      <td>
                        <div className="product-image-cell">
                          {imageUrl ? (
                            <img
                              src={imageUrl}
                              alt={product.name}
                              className="product-thumbnail"
                              onError={(e) => {
                                e.target.style.display = 'none';
                                e.target.nextSibling.style.display = 'flex';
                              }}
                            />
                          ) : null}
                          <div
                            className="product-thumbnail-fallback"
                            style={{ display: imageUrl ? 'none' : 'flex' }}
                          >
                            {emoji}
                          </div>
                          {hasDiscount && (
                            <div className="thumbnail-discount-badge">
                              {product.discount_percent}%
                            </div>
                          )}
                        </div>
                      </td>

                      {/* Name */}
                      <td>
                        <div className="product-name-cell">
                          <div className="product-name">{product.name}</div>
                          {product.description && (
                            <div className="product-description">{product.description}</div>
                          )}
                        </div>
                      </td>

                      {/* Category */}
                      <td>
                        <span className="category-badge">
                          {getCategoryLabel(product.category)}
                        </span>
                      </td>

                      {/* ── Price (inline editable) ── */}
                      <td>
                        {isEditingPrice ? (
                          <div className="inline-edit-wrap">
                            <span className="inline-edit-prefix">Rs.</span>
                            <input
                              ref={inputRef}
                              type="number"
                              min="0"
                              step="0.01"
                              className="inline-edit-input"
                              value={editValue}
                              onChange={e => setEditValue(e.target.value)}
                              onKeyDown={e => handleKeyDown(e, product)}
                              onBlur={() => saveEdit(product)}
                            />
                            <button className="inline-save-btn"  onClick={() => saveEdit(product)} title="Save">✓</button>
                            <button className="inline-cancel-btn" onClick={cancelEdit}              title="Cancel">✕</button>
                          </div>
                        ) : (
                          <div
                            className="price-cell editable-cell"
                            onClick={() => startEdit(product, 'price')}
                            title="Click to edit price"
                          >
                            {hasDiscount ? (
                              <>
                                <span className="price-original">Rs. {product.price}</span>
                                <span className="price-discounted">Rs. {discountedPrice}</span>
                                <span className="unit">/{product.unit}</span>
                              </>
                            ) : (
                              <>
                                <span className="price-main">Rs. {product.price}</span>
                                <span className="unit">/{product.unit}</span>
                              </>
                            )}
                            <span className="edit-pencil">✏️</span>
                          </div>
                        )}
                      </td>

                      {/* ── Discount (inline editable) ── */}
                      <td>
                        {isEditingDiscount ? (
                          <div className="inline-edit-wrap">
                            <input
                              ref={inputRef}
                              type="number"
                              min="0"
                              max="100"
                              step="1"
                              className="inline-edit-input"
                              value={editValue}
                              onChange={e => setEditValue(e.target.value)}
                              onKeyDown={e => handleKeyDown(e, product)}
                              onBlur={() => saveEdit(product)}
                            />
                            <span className="inline-edit-suffix">%</span>
                            <button className="inline-save-btn"  onClick={() => saveEdit(product)} title="Save">✓</button>
                            <button className="inline-cancel-btn" onClick={cancelEdit}              title="Cancel">✕</button>
                          </div>
                        ) : (
                          <div
                            className="discount-cell editable-cell"
                            onClick={() => startEdit(product, 'discount_percent')}
                            title="Click to edit discount"
                          >
                            {hasDiscount ? (
                              <span className="discount-tag">-{product.discount_percent}%</span>
                            ) : (
                              <span className="no-discount">No discount</span>
                            )}
                            <span className="edit-pencil">✏️</span>
                          </div>
                        )}
                      </td>

                      {/* Stock */}
                      <td>
                        <span className={`stock-badge ${product.stock <= 5 ? 'low-stock' : ''}`}>
                          {product.stock} {product.unit}
                        </span>
                      </td>

                      {/* Status toggle */}
                      <td>
                        <button
                          className={`status-toggle-btn ${product.is_active ? 'status-active' : 'status-inactive'} ${isToggling ? 'status-toggling' : ''}`}
                          onClick={() => handleToggleStatus(product)}
                          disabled={isToggling}
                          title={product.is_active ? 'Click to Deactivate' : 'Click to Activate'}
                        >
                          {isToggling ? (
                            <span className="toggle-spinner" />
                          ) : (
                            <>
                              <span className="toggle-dot" />
                              {product.is_active ? 'Active' : 'Inactive'}
                            </>
                          )}
                        </button>
                      </td>

                      {/* Actions */}
                      <td>
                        <div className="action-buttons">
                          <button
                            className="btn-icon btn-edit"
                            onClick={() => navigate(`/admin/products/edit/${product.id}`)}
                            title="Edit"
                          >
                            ✏️
                          </button>
                          <button
                            className="btn-icon btn-delete"
                            onClick={() => handleDelete(product.id, product.name)}
                            title="Delete"
                          >
                            🗑️
                          </button>
                        </div>
                      </td>

                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}